import { encode, decode } from '@msgpack/msgpack'

import { getTranscriptServerUrl } from 'src/utils/env'
import { EditPermissions, EditPermissionsJSON } from 'src/models/EditPermissions'
import { TermBoostingParams } from 'src/models/TermBoosting'

import { CrdtSyncResponse } from './types'
import { TranscriptHttpClientInterface } from './HttpClientInterface'
import { HttpClientBase } from './HttpClientBase'

/**
 * Error thrown by raw-fetch methods (crdtSync) to maintain a consistent
 * `.response.status` shape that RtBrokerTranscriptClientV2 can classify.
 */
export class FetchHttpError extends Error {
    readonly response: { status: number }

    constructor(status: number, message?: string) {
        super(message ?? `HTTP error ${status}`)
        this.name = 'FetchHttpError'
        this.response = { status }
    }
}

export class TranscriptHttpClient extends HttpClientBase implements TranscriptHttpClientInterface {
    constructor() {
        super({
            baseURL: `${getTranscriptServerUrl()}/api/v1/`,
        })
    }

    async boostTerm(params: TermBoostingParams) {
        return this.post('/term_boosting', {
            withCredentials: true,
            requiresAuth: true,
            params,
        })
    }

    async undoBoostTerm(params: TermBoostingParams) {
        return this.delete('/term_boosting', {
            withCredentials: true,
            requiresAuth: true,
            params,
        })
    }

    async getEditingPermissions(transcriptJobId: string, languageCode: string): Promise<EditPermissions> {
        return this.get<EditPermissionsJSON>(`${transcriptJobId}/${languageCode}/authorize`, {
            baseURL: `${getTranscriptServerUrl()}/api/v2/transcript/`,
            withCredentials: true,
            requiresAuth: true,
        }).then(EditPermissions.fromJson)
    }

    async getSlotPath(jobId: string, languageCode: string): Promise<{ path: string; slot: number }> {
        return this.get<{ path: string; slot: number }>(`/doc/${jobId}/${languageCode}/route`, {
            withCredentials: true,
            requiresAuth: false,
        })
    }

    /**
     * CRDT state sync. Uses raw fetch (not axios) because the request/response
     * are msgpack-encoded binary, not JSON.
     */
    async crdtSync(jobId: string, languageCode: string, stateVector: Uint8Array): Promise<CrdtSyncResponse> {
        const url = `${getTranscriptServerUrl()}/api/v2/transcript/${jobId}/${languageCode}/sync`
        const body = encode({ stateVector })

        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/msgpack' },
            credentials: 'include',
            body,
        })

        if (!response.ok) {
            throw new FetchHttpError(response.status)
        }

        const buffer = await response.arrayBuffer()
        const decoded = decode(new Uint8Array(buffer)) as {
            delta: Uint8Array
            serverStateVector: Uint8Array
            lastTimestamp: number
        }

        return {
            delta: new Uint8Array(decoded.delta),
            serverStateVector: new Uint8Array(decoded.serverStateVector),
            lastTimestamp: decoded.lastTimestamp,
        }
    }
}
